import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types'; // Add this import
import { BarChart, LineChart, PieChart, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Bar, Line, Pie, Cell, ResponsiveContainer } from 'recharts';
import { formatDate, formatNumber, sortData } from './dataProcessing';
import { ArrowUpDown, TrendingUp } from 'lucide-react';
import { currentConfig } from '../../contexts/ConfigContext';
import { calculateTrendLine, getTrendLineStyle } from './chartUtils';

const COLORS = ['#8884d8', '#FFADFF', '#170DF2', '#ff8042', '#0088FE', '#00C49F'];
const PREDICTION_COLOR = '#14B8A6'; // Elegant teal for forecasts
const PREDICTION_OPACITY = 0.6;

// Map predictor names to colors from the existing COLORS array
const getPredictorColor = (predictorName) => {
  const predictorOrder = {
    'Primary': 0, // Use PREDICTION_COLOR
    'Linear Regression': 1,
    'ARIMA': 2,
    'Moving Average': 3,
    'Exponential Smoothing': 4
  };
  
  if (predictorName === 'Primary') {
    return PREDICTION_COLOR; // Use elegant teal for primary
  }
  
  const index = predictorOrder[predictorName];
  return index !== undefined ? COLORS[index % COLORS.length] : COLORS[0];
};

const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize="12"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const ChartView = ({ 
  data, 
  schema, 
  chartType, 
  selectedXAxis, 
  selectedYAxis, 
  sortColumn, 
  sortOrder, 
  onYAxisRemove,
  syncScales,
  onSyncScalesChange,
  isNumberFormatted,  
  transformationType,
  showTrendLine,
  onToggleTrendLine,
  isFullscreen = false
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (transformationType) {
      console.log(`ChartView handling ${transformationType} data`);
      // You may need additional logic here depending on how your transformed data
      // needs to be processed for charts
    }
  }, [transformationType]);

   // Helper to detect if a column is a forecast column
  const isForecastColumn = (columnName) => {
    return columnName && (
      columnName.endsWith(' - forecast') || 
      columnName.includes(' - forecast (')
    );
  };

  // Helper to check if this is the primary forecast
  const isPrimaryForecast = (columnName) => {
    return columnName && columnName.endsWith(' - forecast') && !columnName.includes('(');
  };

  // Helper to get predictor name from forecast column
  const getPredictorName = (columnName) => {
    const match = columnName.match(/- forecast \(([^)]+)\)$/);
    return match ? match[1] : 'Primary';
  };

  // Helper to get the original column name from a forecast column
  const getOriginalColumn = (forecastColumn) => {
    return forecastColumn.replace(' - forecast', '');
  };
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    // Filter out trend lines completely
    const filteredPayload = payload.filter(entry => {
      if (!entry || !entry.dataKey) return false;
      // Make sure dataKey is treated as a string before using endsWith
      return !String(entry.dataKey).endsWith('Trend');
    });
    
    if (filteredPayload.length === 0) return null;
    
    return (
      <div className="custom-tooltip" style={{ 
        backgroundColor: 'white', 
        padding: '8px 10px',
        borderRadius: '6px',
        boxShadow: '0 3px 14px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        <div style={{ 
          fontWeight: '600', 
          fontSize: '13px', 
          color: '#333',
          marginBottom: '6px',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: '4px'
        }}>
          {formatAxisValue(label, selectedXAxis)}
        </div>
        {filteredPayload.map((entry, index) => {
          // Get formatted value
          let displayValue;
          const exemptedColumns = currentConfig?.processing?.exemptedColumns || [];
          
          if (exemptedColumns.includes(entry.dataKey)) {
            displayValue = entry.value;
          } else {
            displayValue = isNumberFormatted 
              ? formatNumber(entry.value, entry.dataKey)
              : (entry.value !== null && entry.value !== undefined
                  ? entry.value.toLocaleString('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    }) 
                  : 'N/A');
          }
          
          return (
            <div key={`item-${index}`} style={{ 
              display: 'flex',
              alignItems: 'center',
              marginTop: index > 0 ? '4px' : '0',
              fontSize: '12px'
            }}>
              <span style={{ 
                width: '8px',
                height: '8px',
                marginRight: '6px',
                backgroundColor: entry.color,
                borderRadius: '2px'
              }}/>
              <span style={{ 
                marginRight: '4px', 
                fontWeight: '500',
                color: '#555'
              }}>
                {formatAxisLabel(entry.dataKey)}:
              </span>
              <span style={{ fontWeight: '600' }}>{displayValue}</span>
              {isForecastColumn(entry.dataKey) && (
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '10px', 
                  color: PREDICTION_COLOR,
                  fontWeight: '500'
                }}>
                  (Forecast)
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  
  const getSchemaType = (fieldName) => {
    const field = schema?.fields?.find(f => f.name === fieldName);
    return field ? field.type : null;
  };

  const formatAxisValue = (value, axisName) => {
    const schemaType = getSchemaType(axisName);
    if (schemaType && schemaType.toLowerCase().includes('date')) {
      // If the value is already in YYYY-MM-DD format, parse and format it
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-');
        return `${day}/${month}/${year}`;
      }
      return formatDate(value, schemaType);
    } else if (schemaType && ['INTEGER', 'FLOAT', 'NUMERIC'].includes(schemaType.toUpperCase())) {
      return isNumberFormatted ? formatNumber(value, axisName) : value;
    }
    return value;
  };

  const formatAxisLabel = (label) => {
    if (!label) return 'Unknown';
    const labelStr = String(label); // Convert label to string
    return labelStr
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const yAxes = Array.isArray(selectedYAxis) ? selectedYAxis : [selectedYAxis];
  console.log('yAxes order:', yAxes);


  const validData = useMemo(() => {
    if (!Array.isArray(data) || !selectedXAxis || !yAxes.length) return [];
  
    // For pivot/transposed data, we may need different handling
    if (transformationType) {
      // This is a basic example - you'll need to tailor this to your specific data structure
      const processedData = data
        .map(item => {
          // Create a new object
          const newItem = {};
          
          // Always include X axis
          newItem[selectedXAxis] = {
            displayValue: item[selectedXAxis]?.value ?? item[selectedXAxis]?.displayValue ?? item[selectedXAxis],
            sortValue: item[selectedXAxis]?.sortValue ?? item[selectedXAxis]?.value ?? item[selectedXAxis]
          };
  
          // Process Y axes
          yAxes.forEach(axis => {
            newItem[axis] = {
              displayValue: item[axis]?.displayValue ?? item[axis],
              sortValue: item[axis]?.sortValue ?? item[axis]
            };
          });
  
          return newItem;
        });
      
      const sortedData = sortData(processedData, sortColumn, sortOrder);
  
      // Transform for chart display
      return sortedData.map(item => {
        const transformedItem = {};
        
        // For X-axis, check if it's a date field and use sortValue if available
        const xAxisField = schema?.fields?.find(f => f.name === selectedXAxis);
        const isDateField = xAxisField && (
          xAxisField.type.toLowerCase().includes('date') || 
          xAxisField.type.toLowerCase().includes('timestamp')
        );
        
        // Just use the displayValue which already has the correct format
        transformedItem[selectedXAxis] = item[selectedXAxis]?.displayValue ?? item[selectedXAxis];
        
        yAxes.forEach(axis => {
          const rawValue =
            item[axis]?.sortValue ??
            item[axis]?.displayValue ??
            item[axis];
  
          const num = Number(rawValue); 
          transformedItem[axis] = Number.isFinite(num) ? num : null;
        });
        
        // Preserve prediction marker
        if (item._isPrediction) {
          transformedItem._isPrediction = true;
        }
        
        return transformedItem;
      });
    }
  
    // Original data processing for non-transformed data
    const processedData = data
      .map(item => {
        // Create a new object with the same structure as TableView
        const newItem = {};
        
        // Preserve prediction marker
        if (item._isPrediction) {
          newItem._isPrediction = true;
        }
        
        // Process X axis
        newItem[selectedXAxis] = {
          displayValue: item[selectedXAxis]?.value ?? item[selectedXAxis]?.displayValue ?? item[selectedXAxis],
          sortValue: item[selectedXAxis]?.sortValue ?? item[selectedXAxis]?.value ?? item[selectedXAxis]
        };
  
        // Process Y axes
        yAxes.forEach(axis => {
          newItem[axis] = {
            displayValue: item[axis]?.displayValue ?? item[axis],
            sortValue: item[axis]?.sortValue ?? item[axis]
          };
        });
  
        return newItem;
      });
  
    // Use the same sortData function as TableView
    const sortedData = sortData(processedData, sortColumn, sortOrder);
  
    // Transform for chart display
    return sortedData.map(item => {
      const transformedItem = {};
      
      // For X-axis, check if it's a date field and use sortValue if available
      const xAxisField = schema?.fields?.find(f => f.name === selectedXAxis);
      const isDateField = xAxisField && (
        xAxisField.type.toLowerCase().includes('date') || 
        xAxisField.type.toLowerCase().includes('timestamp')
      );
      
      transformedItem[selectedXAxis] = item[selectedXAxis]?.displayValue ?? item[selectedXAxis];

      
      yAxes.forEach(axis => {
        const rawValue =
          item[axis]?.sortValue ??
          item[axis]?.displayValue ??
          item[axis];
  
        const num = Number(rawValue); 
        transformedItem[axis] = Number.isFinite(num) ? num : null;
      });
      
      // Preserve prediction marker
      if (item._isPrediction) {
        transformedItem._isPrediction = true;
      }
      
      return transformedItem;
    });
  }, [data, selectedXAxis, yAxes, sortColumn, sortOrder, transformationType, schema]);

// Calculate trend lines for each y-axis if enabled
const trendLines = useMemo(() => {
  if (!showTrendLine || !validData || validData.length < 2) {
    return {};
  }

  const trends = {};
  
  // Group actual columns with their forecast pairs
  const processedColumns = new Set();
  
  yAxes.forEach(axis => {
    // Skip if already processed as part of a pair
    if (processedColumns.has(axis)) return;
    
    // Check if this is a forecast column
    if (isForecastColumn(axis)) {
      const originalColumn = getOriginalColumn(axis);
      
      // If we also have the original column selected, skip this
      // (we'll process it when we hit the original column)
      if (yAxes.includes(originalColumn)) {
        return;
      }
    }
    
    // Check if this column has a forecast pair that's also selected
    const forecastColumn = `${axis} - forecast`;
    const hasForecastPair = yAxes.includes(forecastColumn);
    
    // Calculate trend for this column (will include forecast data if pair exists)
    const trendData = calculateTrendLine(validData, selectedXAxis, axis, chartType, yAxes);
    
    if (trendData) {
      trends[axis] = {
        data: trendData,
        isCombined: hasForecastPair
      };
      
      // Mark both columns as processed if we have a pair
      processedColumns.add(axis);
      if (hasForecastPair) {
        processedColumns.add(forecastColumn);
      }
    }
  });
  
  return trends;
}, [showTrendLine, validData, selectedXAxis, yAxes, chartType]);

// Add combined chart data that includes trend lines
const chartData = useMemo(() => {
  // Start with the base valid data
  let result = [...validData];
  
  // Add trend line data if enabled
  if (showTrendLine && validData && validData.length >= 2 && yAxes.length > 0) {
    const primaryAxis = yAxes[0];
    const trendInfo = trendLines[primaryAxis];
    
    if (trendInfo && trendInfo.data) {
      // Merge trend data into the main dataset
      result = result.map((point, index) => {
        if (index < trendInfo.data.length) {
          return {
            ...point,
            [`${primaryAxis}Trend`]: trendInfo.data[index][`${primaryAxis}Trend`]
          };
        }
        return point;
      });
    }
  }
  
  return result;
}, [validData, showTrendLine, trendLines, yAxes]);

  const getYDomain = (axis) => {
    if (validData.length === 0) return [0, 1];
  
    if (syncScales && yAxes.length > 1) {
      const allValues = validData.flatMap(item => 
        yAxes.map(yAxis => item[yAxis])
      ).filter(val => typeof val === 'number' && !isNaN(val));
      
      const minValue = Math.min(...allValues);
      const maxValue = Math.max(...allValues);
  
      if (minValue === maxValue) {
        return [0, maxValue * 1.1];
      }
  
      const padding = (maxValue - minValue) * 0.1;
      
      // For bar charts, always start from 0
      if (chartType === 'bar') {
        return [0, maxValue + padding];
      }
      
      // For line charts, use adaptive scaling
      return [Math.max(0, minValue - padding), maxValue + padding];
    } else {
      const values = validData.map(item => item[axis])
        .filter(val => typeof val === 'number' && !isNaN(val));
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
  
      if (minValue === maxValue) {
        return [0, maxValue * 1.1];
      }
  
      const padding = (maxValue - minValue) * 0.1;
      
      // For bar charts, always start from 0
      if (chartType === 'bar') {
        return [0, maxValue + padding];
      }
      
      // For line charts, use adaptive scaling
      return [Math.max(0, minValue - padding), maxValue + padding];
    }
  };

  const getChartHeight = () => {
    if (isFullscreen) {
      // Use most of the available height in fullscreen
      return window.innerHeight - 250; // Account for modal header and controls
    }
    const baseHeight = 450;
    return isMobile ? baseHeight * 0.7 : baseHeight * 0.8;
  };

  const commonProps = {
    data: chartData, // Use combined data that includes trend lines
    margin: isMobile
      ? { top: 60, right: 30, left: 30, bottom: 40 }
      : { top: 60, right: 40, left: 40, bottom: 40 }
  };

  const commonAxisProps = {
    tickFormatter: (value) => formatAxisValue(value, selectedXAxis),
    angle: -45,
    textAnchor: "end",
    height: 60,
    style: { fontSize: '12px', fontFamily: 'inherit' }
  };

  const handleAxisRemove = (axis) => {
    console.log('Removing axis:', axis);
    console.log('Current yAxes:', yAxes);
    // Only allow removal if there will be at least one axis remaining
    if (yAxes.length > 1 && typeof onYAxisRemove === 'function') {
      onYAxisRemove(axis);
    }
  };

  // Check if we have enough data for trend lines
  const hasTrendData = useMemo(() => {
    return validData.length >= 3 && Object.keys(trendLines).length > 0;
  }, [validData.length, trendLines]);
  
  const renderChart = () => {
    switch (chartType) {
      case 'bar':
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {/* Scale Toggle */}
        {yAxes.length > 1 && (
          <button
            onClick={() => onSyncScalesChange && onSyncScalesChange(!syncScales)}
            className={`absolute left-1/2 -translate-x-1/2 top-3 flex items-center gap-1.5 px-2 py-1 rounded 
              transition-colors duration-200 hover:text-secondary
              ${syncScales 
                ? 'bg-gray-700 text-white' 
                : 'text-gray-600 bg-gray-50'} 
              hover:bg-gray-100 active:bg-gray-700 active:text-white`}
            title={syncScales ? "Disable scale comparison" : "Enable scale comparison"}
            style={{ zIndex: 30 }}
          >
            <ArrowUpDown 
              size={13} 
              className={`transition-transform hover:scale-110 ${
                syncScales ? 'stroke-[2.5px]' : 'stroke-[2px]'
              }`}
            />
            <span className="hidden md:inline text-[11px] font-medium">
              {syncScales ? 'Comparing' : 'Compare'}
            </span>
          </button>
        )}

        {/* Left Y-axis label */}
        {yAxes.length > 0 && (
          <div
            style={{
              position: 'absolute',
              left: isMobile ? '4px' : '8px',
              top: '12px',
              zIndex: 20,
              pointerEvents: 'all'
            }}
          >
            <div
              style={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'white',
                backgroundColor: COLORS[0],
                cursor: yAxes.length > 1 ? 'pointer' : 'default',
                padding: '2px 8px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'transform 0.2s ease-in-out'
              }}
              onClick={() => handleAxisRemove(yAxes[1])}
              title={yAxes.length > 1 ? `Click to remove ${formatAxisLabel(yAxes[0])}` : undefined}
              onMouseEnter={(e) => yAxes.length > 1 && (e.currentTarget.style.transform = 'scale(1.05)')}
              onMouseLeave={(e) => yAxes.length > 1 && (e.currentTarget.style.transform = 'scale(1)')}
            >
              {formatAxisLabel(yAxes[0])}
              {yAxes.length > 1 && <span style={{ fontSize: '10px' }}>×</span>}
            </div>
          </div>
        )}

        {/* Right Y-axis label */}
        {yAxes.length > 1 && (
          <div
            style={{
              position: 'absolute',
              right: isMobile ? '4px' : '8px',
              top: '12px',
              zIndex: 20,
              pointerEvents: 'all'
            }}
          >
            <div
              style={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'white',
                backgroundColor: COLORS[1],
                cursor: 'pointer',
                padding: '2px 8px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'transform 0.2s ease-in-out'
              }}
              onClick={() => handleAxisRemove(yAxes[0])}
              title={`Click to remove ${formatAxisLabel(yAxes[1])}`}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {formatAxisLabel(yAxes[1])}
              <span style={{ fontSize: '10px' }}>×</span>
            </div>
          </div>
        )}

        <ResponsiveContainer>
          <ComposedChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={selectedXAxis} {...commonAxisProps} />
            {yAxes.filter(axis => 
              typeof axis === 'string' && validData.length > 0 && Object.keys(validData[0]).includes(axis)
            )
            .slice(0, 2) // Only take the first 2 axes for displaying legends
            .map((axis, index) => (
              <YAxis 
                key={axis}
                yAxisId={axis}
                orientation={index === 0 ? "left" : "right"}
                tickFormatter={(value) => {
                  const exemptedColumns = currentConfig?.processing?.exemptedColumns || [];
                  if (exemptedColumns.includes(axis)) {
                    return value;
                  }
                  return isNumberFormatted ? 
                    formatAxisValue(value, axis) : 
                    value.toLocaleString('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    });
                }}
                style={{ fontSize: '12px', fontFamily: 'inherit' }}
                domain={getYDomain(axis)}
              />
            ))}
          
          <Tooltip 
            cursor={{ 
              fill: 'rgba(0, 0, 0, 0.15)',
              strokeDasharray: null,
              strokeWidth: 0,
              stroke: null
            }}
            wrapperStyle={{ 
              zIndex: 1000,
              transition: 'all 0.15s ease-out',
              outline: 'none'
            }}
            content={<CustomTooltip />}
            formatter={(value, name) => {
              // Skip trend line values in tooltip
              if (name.endsWith('Trend')) {
                return [null, null];
              }
              return [value, name];
            }}
          />
            
            {/* Integrate trend line data with regular data */}
            {showTrendLine && yAxes.length > 0 && validData.length > 0 && (
              <defs>
                <linearGradient id="trendLineGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0.2}/>
                </linearGradient>
              </defs>
            )}

            {/* Render bar components */}
            {yAxes.map((axis, index) => {
            const original = axis.endsWith(' - forecast')
              ? axis.replace(/ - forecast.*$/, '')
              : axis;

            const isForecast = original !== axis;
            const predictorName = getPredictorName(axis);
            
            // Determine fill color based on whether it's a forecast
            const barFillColor = isForecast 
              ? getPredictorColor(predictorName)
              : COLORS[index % COLORS.length];
            
            // Determine the correct yAxisId
            let yAxisId;
            if (index < 2) {
              // Check if the original column is still in yAxes
              if (yAxes.includes(original)) {
                yAxisId = original;
              } else {
                // If original is not in yAxes, use the current axis
                yAxisId = axis;
              }
            } else {
              // For axes beyond the first 2, cycle through available axes
              yAxisId = yAxes[index % Math.min(2, yAxes.length)];
            }

            return (
              <Bar
                key={axis}
                dataKey={axis}
                stackId={original}
                fill={barFillColor}
                stroke={barFillColor}
                strokeWidth={1}
                yAxisId={yAxisId}
                name={axis}
                isAnimationActive={true}
                animationDuration={300}
                activeBar={{ 
                  fill: `${barFillColor}aa`,
                  fillOpacity: 0.9,
                  stroke: null,
                  strokeWidth: 0
                }}
                shape={(props) => {
                  const { fill, x, y, width, height, payload, ...otherProps } = props;
                  const value = payload[axis];
                  const opacity = isForecast ? PREDICTION_OPACITY : 1;
            
                  // Fix for negative values: Recharts sometimes gives us negative height
                  // We need to use absolute height and adjust y position
                  const absHeight = Math.abs(height);
                  const adjustedY = height < 0 ? y - absHeight : y;
            
                  return (
                    <rect
                      {...otherProps}
                      x={x}
                      y={adjustedY}
                      width={width}
                      height={absHeight}
                      fill={barFillColor}
                      fillOpacity={opacity}
                      strokeDasharray={isForecast ? '5 5' : 'none'}
                      stroke={barFillColor}
                      strokeWidth={1}
                    />
                  );
                }}
                label={validData.length <= 12 && yAxes.length <= 2 ? {
                  position: (value) => value >= 0 ? 'top' : 'bottom',
                  fill: '#666',
                  fontSize: 11,
                  formatter: (value) => {
                    const exemptedColumns = currentConfig?.processing?.exemptedColumns || [];
                    if (exemptedColumns.includes(axis)) return value;
                    return isNumberFormatted
                      ? formatNumber(value, axis)
                      : value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 1 });
                  }
                } : false}
              />
            );
          })}


            {/* Add trend line with shared data source */}
            {showTrendLine && yAxes.length > 0 && (
              <Line
                type="monotone"
                dataKey={`${yAxes[0]}Trend`}
                yAxisId={yAxes[0]}
                stroke={COLORS[0]}
                strokeWidth={3}
                strokeDasharray="5 5"
                opacity={0.9}
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                legendType="none"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
  case 'line':
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
          {/* Scale Toggle */}
          {yAxes.length > 1 && (
            <button
              onClick={() => onSyncScalesChange && onSyncScalesChange(!syncScales)}
              className={`absolute left-1/2 -translate-x-1/2 top-3 flex items-center gap-1.5 px-2 py-1 rounded 
                transition-colors duration-200 hover:text-secondary
                ${syncScales 
                  ? 'bg-gray-700 text-white' 
                  : 'text-gray-600 bg-gray-50'} 
                hover:bg-gray-100 active:bg-gray-700 active:text-white`}
              title={syncScales ? "Disable scale comparison" : "Enable scale comparison"}
              style={{ zIndex: 30 }}
            >
              <ArrowUpDown 
                size={13} 
                className={`transition-transform hover:scale-110 ${
                  syncScales ? 'stroke-[2.5px]' : 'stroke-[2px]'
                }`}
              />
              <span className="hidden md:inline text-[11px] font-medium">
                {syncScales ? 'Comparing' : 'Compare'}
              </span>
            </button>
          )}
  
          {/* Left Y-axis label */}
          {yAxes.length > 0 && (
            <div
              style={{
                position: 'absolute',
                left: isMobile ? '4px' : '8px',
                top: '12px',
                zIndex: 20,
                pointerEvents: 'all'
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'white',
                  backgroundColor: COLORS[0],
                  cursor: yAxes.length > 1 ? 'pointer' : 'default',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'transform 0.2s ease-in-out'
                }}
                onClick={() => handleAxisRemove(yAxes[1])}
                title={yAxes.length > 1 ? `Click to remove ${formatAxisLabel(yAxes[0])}` : undefined}
                onMouseEnter={(e) => yAxes.length > 1 && (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => yAxes.length > 1 && (e.currentTarget.style.transform = 'scale(1)')}
              >
                {formatAxisLabel(yAxes[0])}
                {yAxes.length > 1 && <span style={{ fontSize: '10px' }}>×</span>}
              </div>
            </div>
          )}
  
          {/* Right Y-axis label */}
          {yAxes.length > 1 && (
            <div
              style={{
                position: 'absolute',
                right: isMobile ? '4px' : '8px',
                top: '12px',
                zIndex: 20,
                pointerEvents: 'all'
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: 'white',
                  backgroundColor: COLORS[1],
                  cursor: 'pointer',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'transform 0.2s ease-in-out'
                }}
                onClick={() => handleAxisRemove(yAxes[0])}
                title={`Click to remove ${formatAxisLabel(yAxes[1])}`}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {formatAxisLabel(yAxes[1])}
                <span style={{ fontSize: '10px' }}>×</span>
              </div>
            </div>
          )}
  
          <ResponsiveContainer>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey={selectedXAxis} {...commonAxisProps} />
              {yAxes.filter(axis => 
                typeof axis === 'string' && validData.length > 0 && Object.keys(validData[0]).includes(axis)
              )
              .slice(0, 2) // Only take the first 2 axes for displaying legends
              .map((axis, index) => (
                <YAxis 
                  key={axis}
                  yAxisId={axis}
                  orientation={index === 0 ? "left" : "right"}
                  tickFormatter={(value) => {
                    const exemptedColumns = currentConfig?.processing?.exemptedColumns || [];
                    if (exemptedColumns.includes(axis)) {
                      return value;
                    }
                    return isNumberFormatted ? 
                      formatAxisValue(value, axis) : 
                      value.toLocaleString('en-US', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2
                      });
                  }}
                  style={{ fontSize: '12px', fontFamily: 'inherit' }}
                  domain={getYDomain(axis)}
                />
              ))}
              <Tooltip 
                cursor={{ 
                  stroke: 'rgba(0, 0, 0, 0.15)', 
                  strokeWidth: 1,
                  strokeDasharray: '3 3'
                }}
                wrapperStyle={{ 
                  zIndex: 1000,
                  transition: 'all 0.15s ease-out',
                  outline: 'none'
                }}
                content={<CustomTooltip />}
                formatter={(value, name) => {
                  // Skip trend line values in tooltip
                  if (name.endsWith('Trend')) {
                    return [null, null];
                  }
                  return [value, name];
                }}
              />
  
              {/* Render regular data series */}
              {yAxes.map((axis, index) => {
                const isForecast = isForecastColumn(axis);
                const lineColor = isForecast 
                  ? getPredictorColor(getPredictorName(axis))
                  : COLORS[index % COLORS.length];
                
                // Determine the correct yAxisId
                let yAxisId;
                if (index < 2) {
                  yAxisId = axis;
                } else {
                  // For axes beyond the first 2, use the appropriate existing axis
                  yAxisId = yAxes[index % Math.min(2, yAxes.length)];
                }
                  
                return (
                  <Line
                    key={axis}
                    type="monotone"
                    dataKey={axis}
                    yAxisId={yAxisId}
                    name={formatAxisLabel(axis)}
                    connectNulls={true}
                    strokeWidth={2}
                    stroke={lineColor}
                    strokeDasharray={isForecast ? "5 5" : "none"}
                    strokeOpacity={isForecast ? PREDICTION_OPACITY : 1}
                    dot={(props) => {
                      // Don't render dots for null/undefined values
                      if (props.payload[axis] === null || props.payload[axis] === undefined) {
                        return null;
                      }
                      
                      return (
                        <circle
                          cx={props.cx}
                          cy={props.cy}
                          r={isForecast ? 2 : 3}  // Smaller dots for forecasts
                          fill={lineColor}
                          fillOpacity={isForecast ? PREDICTION_OPACITY : 1}
                          strokeWidth={isForecast ? 1 : 0}
                          stroke={isForecast ? lineColor : 'none'}
                        />
                      );
                    }}
                  />
                );
              })}
  
              {/* Render trend lines - only for the first Y-axis when enabled */}
              {showTrendLine && yAxes.length > 0 && chartData.some(item => item[`${yAxes[0]}Trend`] !== undefined) && (
                <Line
                  type="linear"
                  dataKey={`${yAxes[0]}Trend`}
                  yAxisId={yAxes[0]}
                  name={`${formatAxisLabel(yAxes[0])} Trend`}
                  stroke={COLORS[0]}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  opacity={0.7}
                  dot={false}
                  activeDot={false}
                  legendType="none"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );

        case 'pie':
          const primaryYAxis = Array.isArray(selectedYAxis) ? selectedYAxis[0] : selectedYAxis;
          const pieData = validData
            .map(item => ({
              name: String(item[selectedXAxis]),
              value: Number(item[primaryYAxis])
            }))
            .filter(item => !isNaN(item.value) && item.value > 0);
        
          const total = pieData.reduce((sum, item) => sum + item.value, 0);
        
          return (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
              {/* Y-axis label */}
              <div
                style={{
                  position: 'absolute',
                  left: isMobile ? '4px' : '8px',
                  top: '12px',
                  zIndex: 20
                }}
              >
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'white',
                    backgroundColor: COLORS[0],
                    padding: '2px 8px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {formatAxisLabel(primaryYAxis)}
                </div>
              </div>
        
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={isMobile ? "70%" : "80%"}
                    fill="#8884d8"
                    dataKey="value"
                    label={<CustomPieLabel />}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [
                      `${isNumberFormatted ? formatNumber(value, primaryYAxis) : value} (${((value / total) * 100).toFixed(1)}%)`,
                      formatAxisLabel(primaryYAxis)
                    ]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          );
      default:
        return null;
    }
  };

  return (
    <div style={{ width: '100%', height: getChartHeight(), position: 'relative' }}>
      {renderChart()}
    </div>
  );
};

ChartView.propTypes = {
  data: PropTypes.array.isRequired,
  schema: PropTypes.object,
  chartType: PropTypes.oneOf(['bar', 'line', 'pie']).isRequired,
  selectedXAxis: PropTypes.string.isRequired,
  selectedYAxis: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]).isRequired,
  sortColumn: PropTypes.string,
  sortOrder: PropTypes.string,
  onYAxisRemove: PropTypes.func,
  syncScales: PropTypes.bool,
  onSyncScalesChange: PropTypes.func,
  isNumberFormatted: PropTypes.bool,
  transformationType: PropTypes.string,
  showTrendLine: PropTypes.bool,
  onToggleTrendLine: PropTypes.func
};

ChartView.defaultProps = {
  syncScales: false,
  isNumberFormatted: true,
  showTrendLine: true
};

export default ChartView;